import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Text,
  VisuallyHidden,
} from "@telegram-apps/telegram-ui";
import { DialogDescription, DialogTitle } from "@radix-ui/react-dialog";
import { useI18n } from "../../utils/i18n/I18nProvider";
import { trackOnboarding } from "../../utils/analytics/trackingEvents";
import "./Onboarding.css";
import "../Looks/LookSelector.css";
import { LOOKS_SPRITES } from "../../scripts/consts/LOOKS_SPRITES";
import { LookIdEnum } from "../../handlers/types";

interface OnboardingProps {
  isOpen: boolean;
  onClose: () => void;
}

const ONBOARDING_SCREENS = [
  {
    titleKey: "onboarding.earn.text",
    textKey: null,
    image: "/assets/img/onboarding/earn-mana.avif",
    fullWidth: true,
  },
  {
    titleKey: "onboarding.getMana1.title",
    textKey: "onboarding.getMana1.text",
    image: "/assets/img/onboarding/complete-levels.avif",
  },
  {
    titleKey: "onboarding.getMana2.title",
    textKey: "onboarding.getMana2.text",
    image: "/assets/img/onboarding/magic-books.avif",
    fullWidth: true,
  },
 // {
 //   titleKey: "onboarding.token.title",
 //   textKey: "onboarding.token.text",
 //   image: "/assets/img/onboarding/evl-token.avif",
 // },
  {
    titleKey: "onboarding.gifts.title",
    textKey: "onboarding.gifts.text",
    type: "gifts" as const,
    gifts: { coins: 250, mana: 4, manaPerDay: 3 },
  },
];

export function Onboarding({ isOpen, onClose }: OnboardingProps) {
  const { t } = useI18n();
  const [currentIndex, setCurrentIndex] = useState(0);

  // Track onboarding shown when component opens
  useEffect(() => {
    if (isOpen) {
      trackOnboarding.shown();
    }
  }, [isOpen]);

  const currentScreen = ONBOARDING_SCREENS[currentIndex];
  const isLastScreen = currentIndex === ONBOARDING_SCREENS.length - 1;
  const isGifts = (currentScreen as any)?.type === "gifts";

  const handleNext = () => {
    if (isLastScreen) {
      localStorage.setItem("onboarding_shown_v2", "true");
      trackOnboarding.closed({ completed: true, screenIndex: currentIndex });
      onClose();
    } else {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handleClose = () => {
    trackOnboarding.closed({ completed: false, screenIndex: currentIndex });
    setCurrentIndex(0);
    onClose();
  };

  if (!isOpen || !currentScreen) return null;

  return (
    <div className="onboarding-overlay">
      <div className="onboarding-content">
        <h1 className="onboarding-title">{t(currentScreen.titleKey)}</h1>

        {currentScreen.textKey && (
          <Text className="onboarding-text">{t(currentScreen.textKey)}</Text>
        )}

        {currentScreen.image && (
          <div
            className={`onboarding-illustration ${
              currentScreen.fullWidth ? "full-width" : ""
            }`}
          >
            <img
              src={currentScreen.image}
              alt={t(currentScreen.titleKey)}
              className="onboarding-image"
            />
          </div>
        )}

        {isGifts && (
          <div className="onboarding-gifts" aria-label={t("common.gifts")}>
            <div className="onboarding-gift-item">
              <img
                src="/assets/img/ui/coin.webp"
                alt={t("common.coins")}
                className="onboarding-gift-icon"
              />
              <span className="onboarding-gift-value">
                {(currentScreen as any).gifts?.coins}
              </span>
            </div>
            <div className="onboarding-gift-item">
              <img
                src="/assets/img/ui/mana.webp"
                alt={t("common.mana")}
                className="onboarding-gift-icon"
              />
              <span className="onboarding-gift-value">
                {(currentScreen as any).gifts?.mana}
              </span>
            </div>
            <div className="onboarding-gift-item">
              <img
                src="/assets/img/ui/mana.webp"
                alt={t("common.mana")}
                className="onboarding-gift-icon"
              />
              <span className="onboarding-gift-value">
                {(currentScreen as any).gifts?.manaPerDay}
              </span>
              <span className="onboarding-gift-label">
                {t("onboarding.gifts.manaPerDay")}
              </span>
            </div>
            <div className="onboarding-gift-item">
              <div className="look-selector__sprite" aria-hidden="true">
                <div
                  className="look-selector__sprite-frame"
                  style={{
                    background: `url(/assets/img/characters/${
                      LOOKS_SPRITES[LookIdEnum.BASIC]
                    }.webp) -60px -8px`,
                  }}
                />
              </div>
              <span className="onboarding-gift-label">
                {t("game.secretLook")}
              </span>
            </div>
          </div>
        )}

        <Button size="l" onClick={handleNext} className="onboarding-button">
          {isLastScreen ? t("onboarding.letsPlay") : t("common.next")}
        </Button>

        <div className="onboarding-progress">
          {ONBOARDING_SCREENS.map((_, index) => (
            <div
              key={index}
              className={`onboarding-dot ${
                index === currentIndex ? "active" : ""
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
